import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useMemo } from 'react';
import { useSearchTaxonomyNodes } from './useSearchTaxonomyNodes';

const SEARCH_DEBOUNCE_MS = 300;

export const useTreeSearch = (taxonomyId: string | undefined, searchQuery: string) => {
  const debouncedQuery = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS);

  const { results: searchResults, isFetching: isSearchFetching } = useSearchTaxonomyNodes(taxonomyId, debouncedQuery);

  const isSearching = searchQuery.trim().length > 0;
  const isSearchPending = isSearchFetching || (isSearching && searchQuery !== debouncedQuery);

  const searchVisibleIds = useMemo((): Set<string> | undefined => {
    if (!isSearching) return;
    const ids = new Set<string>();
    for (const r of searchResults) {
      ids.add(r.node.id);
      for (const a of r.ancestors) ids.add(a.id);
    }
    return ids;
  }, [isSearching, searchResults]);

  const searchMatchIds = useMemo(
    () => (isSearching ? new Set(searchResults.map(r => r.node.id)) : undefined),
    [isSearching, searchResults]
  );

  const searchExpandedIds = useMemo(() => {
    if (searchResults.length === 0) return [];
    const ancestorIds = searchResults.flatMap(r => r.ancestors.map(a => a.id));
    return [...new Set(ancestorIds)];
  }, [searchResults]);

  return { searchResults, isSearching, isSearchPending, searchVisibleIds, searchMatchIds, searchExpandedIds };
};
